import { useState, useRef, useEffect } from "react";
import WhatsAppMockup from "./WhatsAppMockup";
import CobrancaWhatsAppMockup from "./CobrancaWhatsAppMockup";
import ClientWhatsAppMockup from "./ClientWhatsAppMockup";
import TutorialOverlay from "./TutorialOverlay";
import FlowNode from "./FlowNode";
import cursorPng from "@/assets/1214426.png";

interface FlowEnvironmentProps {
  flowType?: "cobranca" | "ia_integration";
}

const FlowEnvironment = ({ flowType = "cobranca" }: FlowEnvironmentProps) => {
  // zoom e pan responsivos
  const [scale, setScale] = useState(0.6);
  const [pan, setPan] = useState({ x: 705, y: -495 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setScale(0.3); // Zoom menor mobile
        // Centralizar aproximadamente
        setPan({ x: 100, y: -300 });
        setIsMobile(true);
      } else {
        setScale(0.6);
        setPan({ x: 705, y: -495 });
        setIsMobile(false);
      }
    };

    // Configuração inicial
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [showClientMockups, setShowClientMockups] = useState(false);
  const [showTutorial, setShowTutorial] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [isFixed, setIsFixed] = useState(false);
  const [fixedPosition, setFixedPosition] = useState({ left: 0 });
  const [isAnimating, setIsAnimating] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const executeBtnRef = useRef<HTMLButtonElement | null>(null);
  const uiCursorRef = useRef<HTMLDivElement | null>(null);
  const demoStartedRef = useRef(false);
  const transformRef = useRef<HTMLDivElement | null>(null);

  // Redefine WhatsApp e tutorial quando o tipo de fluxo muda
  useEffect(() => {
    setShowWhatsApp(false);
    setShowClientMockups(false);
    setShowTutorial(true);
    demoStartedRef.current = false;
  }, [flowType]);

  // Adicionar listeners para eventos de pan do diagrama
  useEffect(() => {
    const handlePanDiagram = (event: CustomEvent) => {
      const { deltaX, deltaY } = event.detail;
      setPan(prev => ({ x: prev.x + deltaX, y: prev.y + deltaY }));
    };

    const handleResetPan = () => {
      setPan({ x: 705, y: -495 });
    };

    window.addEventListener('panDiagram', handlePanDiagram as EventListener);
    window.addEventListener('resetPan', handleResetPan);

    return () => {
      window.removeEventListener('panDiagram', handlePanDiagram as EventListener);
      window.removeEventListener('resetPan', handleResetPan);
    };
  }, []);

  const handleExecute = () => {
    setShowTutorial(false);
    setExecuting(true);
    // Mostra animação de carregamento por 1.5 segundos antes de mostrar WhatsApp
    setTimeout(() => {
      setExecuting(false);
      setShowWhatsApp(true);
    }, 1500);
  };

  /* ---------------- Animação do cursor de demonstração ---------------- */
  const [demoPlaying, setDemoPlaying] = useState(false);

  // movimentação do cursor
  const animateCursor = (
    element: HTMLElement,
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    duration: number
  ): Promise<void> => {
    return new Promise((resolve) => {
      const startTime = performance.now();

      const step = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const ease =
          progress < 0.5
            ? 2 * progress * progress
            : -1 + (4 - 2 * progress) * progress;

        const x = startX + (endX - startX) * ease;
        const y = startY + (endY - startY) * ease;

        element.style.left = x + "px";
        element.style.top = y + "px";

        if (progress < 1) requestAnimationFrame(step);
        else resolve();
      };

      requestAnimationFrame(step);
    });
  };

  // Animação aprimorada do cursor: seguir mouse -> mover para botão -> clicar
  const runDemoAnimation = async () => {
    if (demoPlaying || !executeBtnRef.current) return;

    setDemoPlaying(true);
    setIsFixed(true); // Fixa o contêiner do diagrama para impedir rolagem

    await new Promise((r) => setTimeout(r, 300));

    // Cria elemento do cursor se não existir
    if (!uiCursorRef.current) {
      const cursorEl = document.createElement("div");
      cursorEl.style.position = "fixed";
      cursorEl.style.width = "32px";
      cursorEl.style.height = "32px";
      cursorEl.style.background = `url(${cursorPng}) center/contain no-repeat`;
      cursorEl.style.transform = "translate(-50%, -50%)";
      cursorEl.style.zIndex = "99999";
      cursorEl.style.pointerEvents = "none";
      cursorEl.style.transition = "left 0.3s ease-out, top 0.3s ease-out, transform 0.15s ease-out";
      document.body.appendChild(cursorEl);
      uiCursorRef.current = cursorEl;
    }

    const uiEl = uiCursorRef.current;
    if (!uiEl) return;

    const container = containerRef.current;
    if (!container) {
      setDemoPlaying(false);
      return;
    }

    // Impede rolagem no contêiner durante a animação
    const preventWheel = (e: WheelEvent) => e.preventDefault();
    const preventTouchMove = (e: TouchEvent) => e.preventDefault();
    container.addEventListener('wheel', preventWheel, { passive: false });
    container.addEventListener('touchmove', preventTouchMove, { passive: false });

    uiEl.style.display = "block";

    // Fase 1: Seguir mouse por 2 segundos
    const handleMouseMove = (e: MouseEvent) => {
      uiEl.style.left = e.clientX + "px";
      uiEl.style.top = e.clientY + "px";
    };

    document.addEventListener('mousemove', handleMouseMove);

    // Após 2 segundos, transitar para o botão
    setTimeout(() => {
      document.removeEventListener('mousemove', handleMouseMove);

      // Fase 2: Mover para posição do botão
      const btnRect = executeBtnRef.current!.getBoundingClientRect();
      const buttonX = btnRect.left + btnRect.width / 2;
      const buttonY = btnRect.top + btnRect.height / 2;

      uiEl.style.left = buttonX + "px";
      uiEl.style.top = buttonY + "px";

      // Fase 3: Simular clique após transição
      setTimeout(() => {
        // Efeito de clique: reduzir escala
        uiEl.style.transform = "translate(-50%, -50%) scale(0.85)";

        setTimeout(() => {
          // Retornar à escala normal
          uiEl.style.transform = "translate(-50%, -50%) scale(1)";

          // Acionar ação do botão
          handleExecute();

          // Ocultar cursor e limpar
          setTimeout(() => {
            uiEl.style.display = "none";
            setDemoPlaying(false);
            setIsFixed(false); // Restaurar comportamento normal
            // Restaurar rolagem
            container.removeEventListener('wheel', preventWheel);
            container.removeEventListener('touchmove', preventTouchMove);
          }, 500);
        }, 150);
      }, 300);
    }, 2000);
  };

  // inicia a demo quando o retângulo dos nodes entrar na área visível
  useEffect(() => {
    demoStartedRef.current = false;

    const handleScroll = () => {
      if (demoStartedRef.current) return;

      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const h = window.innerHeight;

      // quando o retângulo estiver mais ou menos no meio da tela
      const isInView = rect.top < h * 0.65 && rect.bottom > h * 0.25;

      if (isInView) {
        demoStartedRef.current = true;
        runDemoAnimation();
      }
    };

    window.addEventListener("scroll", handleScroll);
    // caso já esteja visível ao carregar
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (uiCursorRef.current && uiCursorRef.current.parentNode) {
        uiCursorRef.current.parentNode.removeChild(uiCursorRef.current);
        uiCursorRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowType]);

  /* ---------------- MOUSE / TOUCH HANDLERS (PANNING) ---------------- */
  const lastTouchRef = useRef<{ x: number; y: number } | null>(null);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    if (isFixed) return; // Se estiver em modo demo/fixo, não permite pan

    // Previne seleção de texto durante o drag
    e.preventDefault();
    document.body.style.userSelect = 'none';

    const startX = e.clientX - pan.x;
    const startY = e.clientY - pan.y;

    const handleMouseMove = (e: MouseEvent) => {
      setPan({ x: e.clientX - startX, y: e.clientY - startY });
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = '';
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isFixed || e.touches.length !== 1) return;
    const touch = e.touches[0];
    lastTouchRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isFixed || !lastTouchRef.current || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - lastTouchRef.current.x;
    const deltaY = touch.clientY - lastTouchRef.current.y;

    setPan(prev => ({ x: prev.x + deltaX, y: prev.y + deltaY }));
    lastTouchRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = () => {
    lastTouchRef.current = null;
  };

  // Nodes for "Cobrança via WhatsApp"
  const cobrancaNodes = [
    // Linha 1: Entrada principal
    {
      id: 14,
      title: "🔥 Gatilho Webhook",
      subtitle: "Webhook",
      icon: "🔗",
      x: -1200,
      y: 600,
    },

    // Linha 2: Primeiras ações
    {
      id: 29,
      title: "WhatsApp API",
      subtitle: "WHATSAPP",
      icon: "📱",
      x: -900,
      y: 600,
    },
    {
      id: 28,
      title: "Messages",
      subtitle: "Executar Querys",
      icon: "📊",
      x: -600,
      y: 600,
    },
    {
      id: 26,
      title: "WhatsApp API",
      subtitle: "WHATSAPP",
      icon: "📱",
      x: -300,
      y: 600,
    },

    // Linha 3: Verificações
    {
      id: 21,
      title: "Check Cliente",
      subtitle: "Executar Querys",
      icon: "🔍",
      x: 0,
      y: 600,
    },
    {
      id: 22,
      title: "Verificar Permissão",
      subtitle: "Condição",
      icon: "⚡",
      x: 300,
      y: 600,
    },

    // Linha 4: Ramificações principais
    {
      id: 11,
      title: "Pergunta",
      subtitle: "VARIAVEL",
      icon: "❓",
      x: 600,
      y: 600,
    },
    { id: 1, title: "Call", subtitle: "Call", icon: "📞", x: 900, y: 600 },
    {
      id: 5,
      title: "Condição",
      subtitle: "Condição",
      icon: "⚡",
      x: 1200,
      y: 600,
    },

    // Linha 5: Ramificação superior direita
    {
      id: 3,
      title: "MS SQL Server",
      subtitle: "Executar Querys",
      icon: "🗄️",
      x: 1500,
      y: 450,
    },
    { id: 10, title: "Agent", subtitle: "Agent", icon: "🤖", x: 1800, y: 450 },
    {
      id: 15,
      title: "Enviar Email",
      subtitle: "Enviar Email",
      icon: "📧",
      x: 2100,
      y: 450,
    },
    {
      id: 13,
      title: "WhatsApp API",
      subtitle: "WHATSAPP",
      icon: "📱",
      x: 2400,
      y: 450,
    },
    {
      id: 20,
      title: "Enviar Email",
      subtitle: "Enviar Email",
      icon: "📧",
      x: 2700,
      y: 450,
    },

    // Linha 6: Ramificação de erro
    {
      id: 32,
      title: "Condição",
      subtitle: "Condição",
      icon: "⚡",
      x: 1500,
      y: 650,
    },
    {
      id: 31,
      title: "WhatsApp API",
      subtitle: "WHATSAPP",
      icon: "📱",
      x: 1800,
      y: 900,
    },
    {
      id: 33,
      title: "WhatsApp - Suporte",
      subtitle: "WHATSAPP",
      icon: "📱",
      x: 2100,
      y: 900,
    },
    {
      id: 34,
      title: "Enviar Email",
      subtitle: "Enviar Email",
      icon: "📧",
      x: 2400,
      y: 900,
    },

    // Linha 7: Tratamento de erro inferior
    {
      id: 17,
      title: "Agent Error",
      subtitle: "Agent",
      icon: "🤖",
      x: 1200,
      y: 900,
    },
    {
      id: 16,
      title: "WhatsApp API",
      subtitle: "WHATSAPP",
      icon: "📱",
      x: 1500,
      y: 900,
    },

    // Linha 8: Checks inferiores (ramificação secundária)
    {
      id: 23,
      title: "WhatsApp API",
      subtitle: "WHATSAPP",
      icon: "📱",
      x: 300,
      y: 900,
    },
    {
      id: 25,
      title: "Check Cliente",
      subtitle: "Executar Querys",
      icon: "🔍",
      x: -900,
      y: 900,
    },
    {
      id: 24,
      title: "Check Documento",
      subtitle: "Executar Querys",
      icon: "📄",
      x: -600,
      y: 900,
    },
    {
      id: 30,
      title: "Check Ocorrência",
      subtitle: "Executar Querys",
      icon: "📋",
      x: -300,
      y: 900,
    },
    {
      id: 35,
      title: "Check Pedido",
      subtitle: "Executar Querys",
      icon: "📦",
      x: 0,
      y: 900,
    },
  ];

  // Nodes for "Integração IA"
  const iaNodes = [
    {
      id: 101,
      title: "Input Usuário",
      subtitle: "Entrada",
      icon: "👤",
      x: -800,
      y: 750,
    },
    {
      id: 102,
      title: "Processamento LLM",
      subtitle: "IA Engine",
      icon: "🧠",
      x: -400,
      y: 750,
    },
    {
      id: 103,
      title: "Validação",
      subtitle: "Check",
      icon: "✅",
      x: 0,
      y: 750,
    },
    {
      id: 104,
      title: "Resposta Gerada",
      subtitle: "Output",
      icon: "💬",
      x: 400,
      y: 750,
    },
    { id: 105, title: "Feedback", subtitle: "Loop", icon: "🔄", x: 0, y: 1000 },
  ];

  // Select nodes based on flowType
  const nodes = flowType === "ia_integration" ? iaNodes : cobrancaNodes;

  // Connections for "Cobrança via WhatsApp"
  const cobrancaConnections = [
    { from: 14, to: 29 },
    { from: 29, to: 28 },
    { from: 28, to: 26 },
    { from: 26, to: 21 },
    { from: 21, to: 22 },
    { from: 22, to: 11 },
    { from: 22, to: 23 },
    { from: 11, to: 1 },
    { from: 1, to: 5 },
    { from: 5, to: 3 },
    { from: 5, to: 17 },
    { from: 3, to: 32 },
    { from: 32, to: 10 },
    { from: 32, to: 31 },
    { from: 10, to: 15 },
    { from: 15, to: 13 },
    { from: 13, to: 20 },
    { from: 17, to: 16 },
    { from: 31, to: 33 },
    { from: 33, to: 34 },
  ];

  // Connections for "Integração IA"
  const iaConnections = [
    { from: 101, to: 102 },
    { from: 102, to: 103 },
    { from: 103, to: 104 },
    { from: 103, to: 105 },
    { from: 105, to: 102 },
  ];

  // Select connections based on flowType
  const connections =
    flowType === "ia_integration" ? iaConnections : cobrancaConnections;

  const getNodeCenter = (nodeId: number) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return { x: 0, y: 0 };
    return { x: node.x + 110, y: node.y + 75 };
  };

  return (
    <div className="relative w-full h-full overflow-hidden font-sans">
      {/* Background com padrão de grade matching website */}
      <div className="absolute inset-0 -z-20 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.25]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.45]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(168, 85, 247, 0.25) 1px, transparent 1px), linear-gradient(90deg, rgba(124, 58, 237, 0.25) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(100%_100%_at_50%_50%,transparent_65%,rgba(30,0,60,0.55)_100%)]" />
      </div>

      {/* Diagrama de Fluxo */}
      <div className="absolute inset-0 z-0">
        <div className={`relative w-full h-full overflow-hidden ${isFixed ? 'fixed top-0 left-0 z-50 pointer-events-none' : ''}`}>
          <div
            ref={containerRef}
            className="relative w-full h-full"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            role="application"
            aria-label="Interactive Flow diagram"
            style={{
              pointerEvents: isFixed ? 'none' : 'all'
            }}
          >
            <div
              id="flow-nodes-area"
              ref={(el) => (transformRef.current = el)}
              className="relative transition-transform duration-100 ease-out"
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
                transformOrigin: "center",
                cursor: "grab",
                width: "100%",
                height: "100%",
              }}
            >
              {/* SVG Connections */}
              <svg
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                  zIndex: 5,
                  overflow: "visible",
                }}
              >
                {connections.map((conn, idx) => {
                  const from = getNodeCenter(conn.from);
                  const to = getNodeCenter(conn.to);
                  const dx = to.x - from.x;
                  const dy = to.y - from.y;
                  const len = Math.sqrt(dx * dx + dy * dy) || 1;
                  const ux = dx / len;
                  const uy = dy / len;
                  const offset = 30;
                  const dotX = to.x - ux * offset;
                  const dotY = to.y - uy * offset;
                  return (
                    <g key={idx}>
                      <line
                        x1={from.x}
                        y1={from.y}
                        x2={to.x}
                        y2={to.y}
                        stroke="#0078D4"
                        strokeWidth="3"
                        strokeLinecap="round"
                        markerEnd="url(#arrowhead)"
                      />
                      <circle cx={dotX} cy={dotY} r={4} fill="#0078D4" />
                    </g>
                  );
                })}
                <defs>
                  <marker
                    id="arrowhead"
                    markerWidth="10"
                    markerHeight="10"
                    refX="9"
                    refY="3"
                    orient="auto"
                  >
                    <polygon points="0 0, 10 3, 0 6" fill="#0078D4" />
                  </marker>
                </defs>
              </svg>

              <div style={{ position: "relative", zIndex: 10 }}>
                {nodes.map((node) => (
                  <FlowNode
                    key={node.id}
                    title={node.title}
                    subtitle={node.subtitle}
                    icon={node.icon}
                    x={node.x}
                    y={node.y}
                    charging={executing}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Conectores para a seção inferior (WhatsApp Mockups) - REMOVIDO */}

      {/* Sobreposição de Tutorial Overlay */}
      {showTutorial && <TutorialOverlay />}

      {/* Sobreposição do Mockup WhatsApp - Cobrança */}
      {showWhatsApp && flowType === "cobranca" && (
        <>
          <CobrancaWhatsAppMockup
            onClose={() => {
              setShowWhatsApp(false);
              setShowClientMockups(false);
              setShowTutorial(true);
            }}
            onComplete={() => {
              setShowClientMockups(true);
              // Fecha os mockups de cliente após 20 segundos
              setTimeout(() => {
                setShowClientMockups(false);
              }, 20000);
            }}
          />
          {showClientMockups && (
            <>
              <div className="absolute bottom-[230px] md:bottom-[250px] left-[5px] md:left-auto md:right-[480px] z-[10006]">
                <ClientWhatsAppMockup clientName="Cliente 1" delay={0} />
              </div>
              <div className="absolute bottom-[240px] md:bottom-[250px] left-[15px] md:left-auto md:right-[640px] z-[10006]">
                <ClientWhatsAppMockup clientName="Cliente 2" delay={200} />
              </div>
              <div className="absolute bottom-[250px] md:bottom-[250px] left-[25px] md:left-auto md:right-[800px] z-[10006]">
                <ClientWhatsAppMockup clientName="Cliente 3" delay={400} />
              </div>
            </>
          )}
        </>
      )}

      {/* Sobreposição do Mockup WhatsApp - Integração IA */}
      {showWhatsApp && flowType === "ia_integration" && (
        <WhatsAppMockup
          onClose={() => {
            setShowWhatsApp(false);
            setShowTutorial(true);
          }}
        />
      )}

      {/* Execute Button */}
      <div
        style={{
          width: "260px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          gap: "10px",
          position: "absolute",
          bottom: "30px",
          left: "calc(50% - 130px)",
          zIndex: 9999,
        }}
      >
        <button
          id="execute-button"
          ref={(el) => (executeBtnRef.current = el)}
          onClick={handleExecute}
          aria-label="Executar fluxo"
          className="group relative px-3 py-2 text-xs md:px-5 md:py-4 md:text-sm"
          style={{
            backgroundColor: "#1976d2",
            border: "none",
            borderRadius: "8px",
            color: "#fff",
            fontWeight: "bold",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            transition: "box-shadow 0.3s ease",
            outline: "none",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            animation: "pulse-glow 2s ease-in-out infinite",
          }}
        >
          <style>{`
            @keyframes pulse-glow {
              0%, 100% {
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
              }
              50% {
                box-shadow: 0 4px 20px rgba(25, 118, 210, 0.6);
              }
            }
          `}</style>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
          EXECUTAR
        </button>
      </div>
    </div>
  );
};

export default FlowEnvironment;
